import styles from './style/sidenav.module.css';
import { NavLink, useLocation } from 'react-router';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';

import React, { ReactNode, useContext, useState } from 'react';

import HomeIcon from '@mui/icons-material/Home';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import ManageFirm from '@mui/icons-material/Groups';
import PaymentIcon from '@mui/icons-material/Payment';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import UserContext from './UserContext';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpIcon from '@mui/icons-material/Help';
import MailIcon from '@mui/icons-material/Mail';
import XpmIcon from '@mui/icons-material/Power';

import AppConfig from './AppConfig';
import { PHONE_QUERY } from './theme';

import BasReadyLogo from './images/logo_white_long_style_app_store_version.svg';
import { Box, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { hasFeature, isBillingAdmin } from './business/Subscription';

type SideNavItem = {
  icon: ReactNode;
  text: string;
  alt?: string;
  externalLink?: string;
  link?: string;
  /** Whether this user sees the item */
  show: boolean | undefined;
};

function getNavData(user: User | null): SideNavItem[] {
  const hasSubscription = !!user?.firm?.subscription;
  const items: SideNavItem[] = [
    { show: hasSubscription, icon: <HomeIcon />, text: 'Dashboard', link: 'dashboard' },
    { show: hasSubscription, icon: <TravelExploreIcon />, text: 'Organisations', link: 'organisations' },
    { show: !!user, icon: <PaymentIcon />, text: 'Subscription', link: 'subscription' },
    { show: !!user && hasFeature(user, 'Emails'), icon: <MailIcon />, text: 'Emails', link: 'emails' },
    { show: !!user && hasFeature(user, 'XPM'), icon: <XpmIcon />, text: 'XPM', alt: 'Xero Practice Manager', link: 'xpm' },
    { show: !!user, icon: <ManageAccountsIcon />, text: 'User Settings', link: 'user-settings' },
    { show: !!user && isBillingAdmin(user) && hasFeature(user, 'MultiUser'), icon: <ManageFirm />, text: 'Manage Firm', link: 'firm' },
    { show: !!user?.isAdmin, icon: <AdminPanelSettingsIcon />, text: 'Manage', link: 'manage' },
    { show: !user, icon: <LoginIcon />, text: 'Login', externalLink: AppConfig.oauthLoginPath },
  ];
  return items.filter(item => item.show);
}

// Help and Log out sit at the bottom, away from the pages people click all day.
function getBottomNavData(user: User | null): SideNavItem[] {
  const items: SideNavItem[] = [
    { show: true, icon: <HelpIcon />, text: 'Support/Help', link: 'support' },
    { show: !!user, icon: <LogoutIcon />, text: 'Logout', externalLink: AppConfig.logoutPath },
  ];
  return items.filter(item => item.show);
}

function NavItem(props: { item: SideNavItem; selected: boolean; collapsed?: boolean; onClick?: () => void }) {
  const { item, selected, collapsed, onClick } = props;
  const content = (
    <>
      {item.icon}
      <span className={styles.linkText}>{item.text}</span>
    </>
  );
  // When collapsed only the icon shows, so the tooltip and aria-label carry the page name.
  const label = collapsed ? item.text : undefined;
  return (
    <Tooltip title={collapsed ? item.text : item.alt || ''} placement="right">
      {item.link ? (
        <NavLink className={selected ? styles.sideItemSelected : styles.sideItem} to={item.link} onClick={onClick} aria-label={label}>
          {content}
        </NavLink>
      ) : (
        <a className={styles.sideItem} href={item.externalLink} aria-label={label}>
          {content}
        </a>
      )}
    </Tooltip>
  );
}

export default function SideNav() {
  // Phones get a top bar with a menu button instead of a side bar, so pages can use the full width.
  const isMobile = useMediaQuery(PHONE_QUERY);
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const userContext = useContext(UserContext);
  const user = userContext?.user;

  const renderItems = (items: SideNavItem[], options: { collapsed?: boolean; onClick?: () => void } = {}) =>
    items.map(item => (
      <NavItem key={item.text} item={item} selected={location.pathname === `/${item.link}`} collapsed={options.collapsed} onClick={options.onClick} />
    ));

  if (isMobile) {
    const closeMenu = () => setMobileOpen(false);
    return (
      <nav className={styles.topBar}>
        <div className={styles.topBarHeader}>
          <img src={BasReadyLogo} style={{ width: '130px' }} alt="BAS Ready" />
          <IconButton
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            sx={{ color: 'white' }}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </div>
        {mobileOpen && (
          <div className={styles.mobileMenu}>
            {renderItems(getNavData(user), { onClick: closeMenu })}
            {renderItems(getBottomNavData(user), { onClick: closeMenu })}
          </div>
        )}
      </nav>
    );
  }

  return (
    <div className={open ? styles.sidenav : styles.sidenavClosed}>
      <Box display="flex" flexDirection="column" height="100%">
        <div>
          <div style={{ paddingBottom: '20px' }}>
            <img src={BasReadyLogo} style={{ width: '130px' }} alt="BAS Ready" />
          </div>
          {user && (
            <button className={styles.menuBtn} onClick={() => setOpen(!open)} aria-label={open ? 'Collapse menu' : 'Expand menu'}>
              {open ? <KeyboardDoubleArrowLeftIcon /> : <KeyboardDoubleArrowRightIcon />}
            </button>
          )}
          {renderItems(getNavData(user), { collapsed: !open })}
        </div>
        <div style={{ marginTop: 'auto' }}>{renderItems(getBottomNavData(user), { collapsed: !open })}</div>
      </Box>
    </div>
  );
}
